import { ProductCardProps } from '@teko/types/ecommerce';
import { RelatedProductsModal } from 'components/desktop/organisms/RelatedProductsModal';
import { RelatedProductsBottomSheet } from 'components/mobile/organisms/RelatedProductsBottomSheet';
import { useTenantConfig } from 'contexts';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';

type ISelectedProduct = Pick<
  ProductCardProps,
  'sku' | 'imageUrl' | 'name' | 'categories'
>;

export interface RelatedProductState {
  selectedProduct?: ISelectedProduct;
  setSelectedProduct?: React.Dispatch<
    React.SetStateAction<ISelectedProduct | undefined>
  >;
}

interface RelatedProductProps {
  children: React.ReactNode;
}

const RelatedProductContext = React.createContext<RelatedProductState>({});

export const RelatedProductProvider: React.FC<RelatedProductProps> = ({
  children,
}: RelatedProductProps) => {
  const { isMobile } = useTenantConfig();
  const [selectedProduct, setSelectedProduct] = useState<ISelectedProduct>();
  const router = useRouter();

  useEffect(() => {
    // when user direct to new page --> close related products bottom sheet
    setSelectedProduct(undefined);
  }, [router.asPath]);

  return (
    <RelatedProductContext.Provider
      value={{ selectedProduct, setSelectedProduct }}
    >
      {children}
      {!!selectedProduct &&
        (isMobile ? (
          <RelatedProductsBottomSheet
            sku={selectedProduct.sku}
            imageUrl={selectedProduct.imageUrl}
            name={selectedProduct.name}
            categoryId={
              !!selectedProduct?.categories?.length
                ? selectedProduct.categories[
                    selectedProduct?.categories.length - 1
                  ].id
                : undefined
            }
            closeBottomSheet={() => setSelectedProduct(undefined)}
          />
        ) : (
          <RelatedProductsModal
            productInfo={{
              sku: selectedProduct.sku,
              imageUrl: selectedProduct.imageUrl,
              name: selectedProduct.name,
              categoryId: !!selectedProduct?.categories?.length
                ? selectedProduct.categories[
                    selectedProduct?.categories.length - 1
                  ].id
                : undefined,
            }}
            onCloseModal={() => setSelectedProduct(undefined)}
          />
        ))}
    </RelatedProductContext.Provider>
  );
};

export function useRelatedProduct(): RelatedProductState {
  const context = useContext(RelatedProductContext);
  if (!context) {
    throw new Error(
      'useRelatedProduct must be inside a useRelatedProduct with a state value',
    );
  }
  return context;
}
